/**
 * The one surface primitive. Conetec layouts are square-shouldered — a hairline
 * border does the separating, shadows are reserved for things that genuinely
 * lift off the page (dialogs, menus). So this has a border and no shadow, and
 * there is no `elevated` prop to reach for.
 */
export function Card({
  title,
  action,
  children,
  className = '',
}: {
  title?: React.ReactNode
  action?: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  return (
    <section className={`card ${className}`.trim()}>
      {(title || action) && (
        <header className="card__head">
          {title && <h2 className="card__title">{title}</h2>}
          {action && <div className="card__action">{action}</div>}
        </header>
      )}
      <div className="card__body">{children}</div>
    </section>
  )
}

/**
 * The link from a rail widget to the screen behind it. SHL-13.
 *
 * David, 14 Sep: "Widgets always have a link to their own screen when one
 * exists (wiki widget, redemption widget, etc.)" A standing rule rather than
 * one widget, so it is one component rather than a link written per card.
 *
 * ── IT GOES LAST IN THE HEADER, AND THAT IS WHAT MAKES IT A RULE ────────────
 *
 * `.card__action` is an inline-flex row pushed to the end, so whatever else a
 * header carries — Wiki One's "3 recent" pill — the link rendered after it
 * still lands against the same edge on every card. Put it first and its
 * position moves with the length of whatever sits beside it, and a control in
 * a different place on each card is one people stop looking for.
 *
 * ── A WIDGET WITH NO SCREEN RENDERS NO LINK, NOT A DISABLED ONE ─────────────
 *
 * There is no `disabled` prop here and there must not be. Trending tags has
 * nowhere to go until browse-by-tag exists (FED-18b), and a greyed-out "See
 * all" is the "My Dashboard" mistake at a smaller scale: a promise the card
 * makes and cannot keep, sitting in front of ~900 people. A card with nothing
 * behind it simply omits this.
 *
 * A plain anchor, not react-router's Link: the app is a HashRouter, every
 * notice already links this way, and an anchor renders without router context —
 * which is what lets a widget be mounted in the render suite at all.
 */
export function CardLink({ to, label }: { to: string; label: string }) {
  return (
    <a className="cardlink" href={to}>
      {label}
    </a>
  )
}
